/**
 * Position math for a floating on-page control (the widget's draggable
 * launcher today; the extension 0.2.0 floating control next). Pure
 * functions over sizes and coordinates. DOM event wiring lives in the shared
 * React shell, while the widget launcher consumes these same
 * primitives directly. The clamp rules, snap rules, and stored-position
 * format therefore cannot drift between surfaces.
 */

/** Pointer movement, in px, before a press becomes a drag rather than a click. */
export const DRAG_THRESHOLD_PX = 4;

/** Distance from a viewport edge, in px, within which the control snaps flush. */
export const EDGE_SNAP_PX = 32;

/** Keep at least this much of the control on screen. */
export const MIN_VISIBLE_PX = 24;

export interface ViewportSize { width: number; height: number }

/**
 * Clamp a top-left placement so the control stays reachable.
 *
 * The rule in both axes: at least `minVisiblePx` of the control stays
 * on screen, and it is never pushed off the top-left. When the control
 * is larger than the viewport, the lower bound wins so it stays
 * reachable rather than being clamped to a negative coordinate.
 *
 * (An earlier inline version clamped vertically with an expression that
 * reduced to a no-op and could strand a control taller than a short
 * viewport at a negative top — the explicit bounds here are the fix,
 * moved verbatim from the widget launcher.)
 */
export function clampToViewport(
  left: number,
  top: number,
  size: { width: number; height: number },
  viewport: ViewportSize,
  minVisiblePx: number = MIN_VISIBLE_PX,
): { left: number; top: number } {
  const minLeft = Math.min(0, viewport.width - size.width);
  const maxLeft = Math.max(minLeft, viewport.width - minVisiblePx);
  const minTop = Math.min(0, viewport.height - size.height);
  const maxTop = Math.max(minTop, viewport.height - minVisiblePx);
  return {
    left: Math.min(Math.max(left, minLeft), maxLeft),
    top: Math.min(Math.max(top, minTop), maxTop),
  };
}

/**
 * Edge snapping on drag release: within `snapPx` of a viewport edge the
 * control lands flush at the configured offset for that edge; otherwise
 * the released position stands. Left/top edges win over right/bottom
 * when both are in range (a control smaller than 2 x snapPx).
 */
export function snapToEdges(
  rect: { left: number; top: number; right: number; bottom: number; width: number; height: number },
  viewport: ViewportSize,
  offsetX: number,
  offsetY: number,
  snapPx: number = EDGE_SNAP_PX,
): { left: number; top: number } {
  let left = rect.left;
  let top = rect.top;
  const rightGap = viewport.width - rect.right;
  const bottomGap = viewport.height - rect.bottom;

  if (rect.left <= snapPx) left = offsetX;
  else if (rightGap <= snapPx) left = viewport.width - rect.width - offsetX;
  if (rect.top <= snapPx) top = offsetY;
  else if (bottomGap <= snapPx) top = viewport.height - rect.height - offsetY;

  return { left, top };
}

/**
 * A persisted control position. Stored as viewport RATIOS, not pixels,
 * so it stays proportionally where the visitor put it across resizes
 * and devices. `version` is the owner's positionVersion: bumping it in
 * config retires every visitor's saved position (otherwise an owner's
 * repositioning would be invisible to every returning visitor).
 */
export interface StoredFloatingPosition {
  xRatio: number;
  yRatio: number;
  version: number;
}

export function toStoredPosition(
  left: number,
  top: number,
  viewport: ViewportSize,
  version: number,
): StoredFloatingPosition {
  return {
    xRatio: left / Math.max(1, viewport.width),
    yRatio: top / Math.max(1, viewport.height),
    version,
  };
}

/**
 * Parse + validate a raw stored-position string. Returns null for
 * anything unusable: missing, malformed JSON, non-numeric ratios, or a
 * version that no longer matches (the owner moved the default since it
 * was saved). Never throws.
 */
export function parseStoredPosition(
  raw: string | null,
  expectedVersion: number,
): StoredFloatingPosition | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredFloatingPosition;
    if (typeof parsed?.xRatio !== 'number' || !Number.isFinite(parsed.xRatio)) return null;
    if (typeof parsed.yRatio !== 'number' || !Number.isFinite(parsed.yRatio)) return null;
    if (parsed.version !== expectedVersion) return null;
    return parsed;
  } catch {
    return null;
  }
}
